import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Lock, Eye, UserCheck, Database, Mail, ArrowLeft, Trash2 } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation("common");
  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4">
      <div className="w-full max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col items-center space-y-3 mb-8 pt-8">
          <img src="/tool.png" alt="Moubarati Logo" className="h-16 w-16" />
          <h1 className="text-4xl font-bold text-white">
            Moubarati - مباراتي
          </h1>
          <p className="text-gray-400 text-lg">{t("privacy.page_subtitle")}</p>
          <p className="text-gray-500 text-sm">{t("privacy.last_updated")}</p>
        </div>

        {/* Introduction */}
        <div className="bg-gradient-to-br from-brand-primary/20 to-brand-secondary/10 border border-brand-primary/30 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-start space-x-4 rtl:space-x-reverse">
            <Shield className="h-8 w-8 text-brand-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-3">
                {t("privacy.commitment_title")}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.commitment_text")}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">

          {/* Section 1: Data Collection */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section1_title")}
              </h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-4">
              {t("privacy.section1_intro")}
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">
              {t("privacy.section1_common_title")}
            </h3>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t("privacy.section1_personal")}
                </h3>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ms-4">
                  {(t("privacy.section1_personal_items", { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t("privacy.section1_activity")}
                </h3>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ms-4">
                  {(t("privacy.section1_activity_items", { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t("privacy.section1_technical")}
                </h3>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ms-4">
                  {(t("privacy.section1_technical_items", { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">
              {t("privacy.section1_apps_title")}
            </h3>
            <div className="space-y-4">
              {(t("privacy.apps", { returnObjects: true }) as { name: string; data: string[] }[]).map((app, i) => (
                <div key={i} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                  <h4 className="font-semibold text-white mb-2">{app.name}</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 ms-4">
                    {app.data.map((d, j) => (
                      <li key={j}>{d}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2: Data Usage */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Eye className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section2_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section2_intro")}
              </p>

              <ul className="list-disc list-inside text-gray-300 space-y-2 ms-4 mt-3">
                {(t("privacy.section2_items", { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 3: Data Security */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Lock className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section3_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section3_intro")}
              </p>

              <div className="bg-gray-900/50 rounded-lg p-4 mt-3 space-y-2">
                {(t("privacy.section3_items", { returnObjects: true }) as { label: string; text: string }[]).map((item, i) => (
                  <div key={i} className="flex items-start">
                    <span className="text-brand-primary me-2">•</span>
                    <p className="text-gray-300">
                      <span className="font-semibold text-white">{item.label}</span> : {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4: Data Sharing */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <UserCheck className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section4_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section4_intro")}
              </p>

              <ul className="list-disc list-inside text-gray-300 space-y-2 ms-4 mt-3">
                {(t("privacy.section4_items", { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <p className="text-gray-300 leading-relaxed mt-4">
                {t("privacy.section4_processors_intro")}
              </p>
              <div className="bg-gray-900/50 rounded-lg p-4 mt-3 space-y-2">
                {(t("privacy.section4_processors", { returnObjects: true }) as { label: string; text: string }[]).map((item, i) => (
                  <div key={i} className="flex items-start">
                    <span className="text-brand-primary me-2">•</span>
                    <p className="text-gray-300">
                      <span className="font-semibold text-white">{item.label}</span> : {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 5: Your Rights */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section5_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section5_intro")}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                  <h4 className="font-semibold text-white mb-2">{t("privacy.right_access")}</h4>
                  <p className="text-gray-400 text-sm">
                    {t("privacy.right_access_desc")}
                  </p>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                  <h4 className="font-semibold text-white mb-2">{t("privacy.right_rectification")}</h4>
                  <p className="text-gray-400 text-sm">
                    {t("privacy.right_rectification_desc")}
                  </p>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                  <h4 className="font-semibold text-white mb-2">{t("privacy.right_erasure")}</h4>
                  <p className="text-gray-400 text-sm">
                    {t("privacy.right_erasure_desc")}
                  </p>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30">
                  <h4 className="font-semibold text-white mb-2">{t("privacy.right_portability")}</h4>
                  <p className="text-gray-400 text-sm">
                    {t("privacy.right_portability_desc")}
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-brand-primary/10 border border-brand-primary/30 rounded-lg">
                <p className="text-gray-300">
                  {t("privacy.rights_contact")} <a href="mailto:lirf@moubarati.com" className="text-brand-primary hover:text-brand-secondary font-semibold">lirf@moubarati.com</a>
                </p>
              </div>
            </div>
          </div>

          {/* Section 6: Cookies */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section6_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section6_intro")}
              </p>

              <ul className="list-disc list-inside text-gray-300 space-y-1 ms-4 mt-2">
                {(t("privacy.section6_items", { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <p className="text-gray-400 text-sm mt-3">
                {t("privacy.section6_note")}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {t("privacy.section6_mobile_note")}
              </p>
            </div>
          </div>

          {/* Section 7: Data Retention */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section7_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section7_intro")}
              </p>

              <ul className="list-disc list-inside text-gray-300 space-y-2 ms-4 mt-3">
                {(t("privacy.section7_items", { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 7b: Account & Data Deletion */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Trash2 className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.deletion_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.deletion_intro")}
              </p>

              <ul className="list-disc list-inside text-gray-300 space-y-2 ms-4 mt-3">
                {(t("privacy.deletion_items", { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              <p className="text-gray-400 text-sm mt-3">
                {t("privacy.deletion_retained_note")}
              </p>
            </div>
          </div>

          {/* Section 8: Contact */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Mail className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section8_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section8_intro")}
              </p>

              <div className="bg-gray-900/50 rounded-lg p-4 mt-3">
                <p className="text-white font-semibold mb-2">Moubarati - مباراتي</p>
                <p className="text-gray-300">
                  <span className="font-semibold">{t("privacy.section8_email_label")}</span>{' '}
                  <a href="mailto:lirf@moubarati.com" className="text-brand-primary hover:text-brand-secondary">
                    lirf@moubarati.com
                  </a>
                </p>
                <p className="text-gray-400 text-sm mt-3">
                  {t("privacy.section8_response_note")}
                </p>
              </div>
            </div>
          </div>

          {/* Section 9: Updates */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-700/50">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-brand-primary me-3" />
              <h2 className="text-2xl font-bold text-brand-primary">
                {t("privacy.section9_title")}
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-gray-300 leading-relaxed">
                {t("privacy.section9_text")}
              </p>

              <p className="text-gray-400 text-sm mt-3">
                {t("privacy.section9_last_updated")}
              </p>
            </div>
          </div>
        </div>

        {/* Back to Login Button */}
        <div className="text-center py-8">
          <a
            href="/"
            className="inline-flex items-center bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl"
          >
            <ArrowLeft className="me-2 h-5 w-5 rtl:rotate-180" />
            {t("privacy.back_home_btn")}
          </a>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm pb-4">
          <p>{t("privacy.footer_copyright")}</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
